<template>
  <div class="d-flex flex-column justify-content-start align-items-center col-12 position-relative"
       :style="gameplayStyle">
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center col-11 mt-3 mb-4">
      <img
          :src="isFreeUser ? require('@/assets/gameplay/free-logo-sml.png') : require('@/assets/gameplay/vip-logo-sml.png')"
          class="col-4" alt="logo"/>
      <img src="@/assets/gameplay/exit-btn.png" class="col-1" alt="close" @click="handleExit"/>
    </div>

    <div v-if="questionCategoryId === 46" class="d-flex flex-column justify-content-center align-items-end col-12">
      <img src="@/assets/gameplay/volume-banner.png" class="col-5" alt=""/>
    </div>

    <!-- Score Display -->
    <div class="d-flex flex-column justify-content-center align-items-center col-12 mt-4">
      <!-- Incorrect Display -->
      <div
          v-if="showIncorrectDisplay"
          class="d-flex flex-column justify-content-center align-items-center col-5 position-relative incorrect-display-enter"
      >
        <img
            src="@/assets/gameplay/incorrect-clock.png"
            class="col-12 incorrect-clock-img"
            alt=""
        >
        <div class="d-flex flex-column justify-content-center align-items-center col-12 text-white"
             style="position: absolute; top: 36%;">
          <div class="text-center fw-bold text-white incorrect-text" style="font-size: 15px">
            INCORRECT
          </div>
          <div
              class="d-flex justify-content-center align-items-center col-11 text-center fw-bold text-white coins-display"
              style="font-size: 13px">
            +0 <img src="@/assets/gameplay/coins-medium.png" class="col-3 coin-img"/>
          </div>
        </div>
      </div>

      <!-- Correct Display -->
      <div
          v-if="showCorrectDisplay"
          class="d-flex flex-column justify-content-center align-items-center col-5 position-relative correct-display-enter correct-shimmer"
      >
        <img
            src="@/assets/gameplay/correct-clock.png"
            class="col-12 correct-clock-img"
            alt=""
        >
        <div class="d-flex flex-column justify-content-center align-items-center col-12 text-white"
             style="position: absolute; top: 36%;">
          <div class="text-center fw-bold text-white correct-text" style="font-size: 15px">
            CORRECT
          </div>
          <div
              class="d-flex justify-content-center align-items-center col-11 text-center fw-bold text-white correct-coins-display"
              style="font-size: 13px">
            +10 <img src="@/assets/gameplay/coins-medium.png" class="col-3 correct-coin-img"/>
          </div>
        </div>
      </div>

      <!-- Timer -->
      <div v-if="!showCorrectDisplay && !showIncorrectDisplay" class="timer-wrapper">
        <div
            class="timer-animation"
            :style="{
              '--timer-progress': timerProgress + 'deg',
              '--timer-color': timerColor
            }"
        >
          <div class="timer">
            {{ displayTime }}
          </div>
        </div>
      </div>
    </div>

    <!-- Question Section -->
    <div class="d-flex flex-column justify-content-center align-items-center col-12 mt-4">
      <div class="col-5 text-center fw-bold text-white p-2 z-2"
           style="border-radius: 8px; background: #4A5488; backdrop-filter: blur(10px);">
        Question {{ currentQuestionNumber }}/{{ totalQuestions }}
      </div>

      <!-- Show different content based on category -->
      <div v-if="currentCategoryId === 46"
           class="d-flex flex-column justify-content-center align-items-center col-10 p-3 text-center question-container text-white z-1">
        <div class="d-flex flex-column justify-content-center align-items-center col-12 z-3">
          <lottie-player :src="require(`../../lotties/music.json`)" speed="1"
                         autoplay loop :key="componentKey"></lottie-player>
        </div>
      </div>
      <div v-else
           class="d-flex flex-column justify-content-center align-items-center col-10 p-3 text-center question-container text-white z-1">
        {{ currentQuestion }}
      </div>
    </div>

    <!-- Answer Options -->
    <div v-if="answers.length > 0"
         class="d-flex flex-column justify-content-center align-items-center col-12 gap-2 mt-4">
      <div
          v-for="(answer, index) in answers"
          :key="index"
          @click="selectAnswer(answer, index)"
          class="col-8 text-center p-3"
          :class="getAnswerClass(index)"
      >
        {{ answer }}
      </div>
    </div>
    <div v-if="showPopupComponent && isFreeUser"
         class="d-flex flex-column justify-content-center align-items-center col-12"
         style="position: absolute; z-index: 100">
      <PopUpComponent @close="handlePopupClose"/>
    </div>
    <ExitGamePopUp v-if="showExitPopup" @continue="handleContinuePlaying" @exit="handleExitGame"/>
    <!-- Footer -->
    <div class="d-flex flex-column justify-content-center align-items-center col-12 position-absolute game-footer"
         style="bottom: 0;">
      <GamePlayFooterComponent/>
    </div>
  </div>
</template>

<script>
import GamePlayFooterComponent from "@/views/components/GamePlayFooterComponent.vue";
import {mapMutations, mapState} from "vuex";
import {environmentConfig} from "@/config/enviromentConfig";
import TriviaService from "@/api/services/triviaService";
import DepService from "@/api/services/depService";
import {Howl, Howler} from 'howler';
import {mtnServiceIdMap} from "@/config";
import {DotLottieVue} from '@lottiefiles/dotlottie-vue'
import PopUpComponent from "@/views/components/PopUpComponent.vue";
import ExitGamePopUp from "@/views/components/ExitGamePopUp.vue";
import {getSeasonalGenreId} from "@/functions/setSeasonalConfig";
import {beginQuestion} from "@/functions/telemetryCollector";
import {shouldAttachGameEnd} from "@/functions/gameEndCaptcha";

export default {
  name: "GamePlayView",
  components: {
    PopUpComponent,
    ExitGamePopUp,
    GamePlayFooterComponent
  },

  data() {
    return {
      showExitPopup: false,
      showPopupComponent: false,
      componentKey: 1,
      // Timer data
      currentTime: 20,
      maxTime: 20,
      startTime: null,
      animationFrame: null,
      isRunning: false,

      // Question data
      currentQuestion: "",
      answers: [],
      currentQuestionNumber: 1,
      totalQuestions: 7,
      currentCategoryId: null,

      // Answer state
      selectedAnswerIndex: null,
      correctAnswerIndex: null,
      hasAnswered: false,
      showCorrectDisplay: false,
      showIncorrectDisplay: false,
      timerExpired: false,
      correctAnswersCount: 0,

      // Game state
      userAnswer: undefined,
      questionId: undefined,
      userAnswerArray: [],
      actualCorrectAnswer: undefined,
      questionDelayForAnimation: undefined,

      // Audio
      howlerSong: undefined,
      songIsLoading: false
    }
  },

  computed: {
    ...mapState([
      'gameState',
      'user',
      'productId',
      'regularGameCoins',
      'brandConfig',
      'isLoading',
      'userQuestions',
      'questionCategoryId',
      'categoryGameState',
      'token',
      'isFreeUser',
      'currentSeason',
    ]),

    seasonalBackground() {
      const backgrounds = {
        'summer': require('@/assets/Backgrounds/music-trivia-bg-summer.webp'),
        'autumn': require('@/assets/Backgrounds/music-trivia-bg-autumn.webp'),
        'winter': require('@/assets/Backgrounds/music-trivia-bg-winter.webp'),
        'back2school': require('@/assets/Backgrounds/music-trivia-bg-back2school.webp'),
        'vday': require('@/assets/Backgrounds/music-trivia-bg-v-day.webp'),
        'easter': require('@/assets/Backgrounds/music-trivia-bg-easter.webp'),
        'christmas': require('@/assets/Backgrounds/music-trivia-bg-christmas.webp'),
      };
      return backgrounds[this.currentSeason] || backgrounds['summer'];
    },

    gameplayStyle() {
      return {
        height: '97svh',
        backgroundImage: `url(${this.seasonalBackground})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      };
    },

    displayTime() {
      return Math.ceil(this.currentTime);
    },

    timerProgress() {
      return (this.currentTime / this.maxTime) * 360;
    },

    timerColor() {
      const percentage = this.currentTime / this.maxTime;
      if (percentage > 0.5) {
        return '#C32BE2'; // Purple
      } else if (percentage > 0.25) {
        return '#FF6B6B'; // Orange-red
      } else {
        return '#FF0000'; // Red
      }
    }
  },

  methods: {
    ...mapMutations([
      'setGameState',
      'setRegularGameCoins',
      'setIsLoading',
      'setUserQuestions',
      'setCategoryGameState',
      'setQuestionCategoryId',
      'setCurrentSeason'
    ]),
    async handlePopupClose() {
      this.showPopupComponent = false;
      await this.nextQuestion();
    },
    // Answer handling
    getAnswerClass(index) {
      if (!this.hasAnswered) {
        return 'unanswered';
      }

      // If timer expired and user didn't answer, all are shown as wrong
      if (this.timerExpired && this.selectedAnswerIndex === -1) {
        if (index === this.correctAnswerIndex) {
          return 'correct-answer';
        }
        return 'unanswered-disabled';
      }

      if (index === this.correctAnswerIndex) {
        return 'correct-answer';
      }
      if (index === this.selectedAnswerIndex && index !== this.correctAnswerIndex) {
        return 'wrong-answer';
      }
      return 'unanswered-disabled';
    },

    async selectAnswer(answer, index) {
      // Prevent multiple selections
      if (this.hasAnswered) return;
      if (this.timerExpired && index !== -1) return;
      if (!this.isRunning && !this.timerExpired) return;

      // Stop timer and music
      this.stopTimer();
      this.stopSong();

      // Store the selected answer but DON'T mark as answered yet
      this.selectedAnswerIndex = index;
      this.userAnswer = answer;

      // Push to answer array
      this.pushUserAnswerArray();

      // Submit answer and get correct answer FIRST
      await this.submitUserAnswer(index);

      // NOW mark as answered (this triggers the visual update)
      this.hasAnswered = true;

      // Show result animation
      if (this.correctAnswerIndex === this.selectedAnswerIndex && !this.timerExpired) {
        this.showCorrectDisplay = true;
      } else {
        this.showIncorrectDisplay = true;
      }

      // Wait 2 seconds then go to next question
      this.questionDelayForAnimation = setTimeout(async () => {
        // Check if this is question 3 or 6 and user is free
        if (this.isFreeUser && (this.currentQuestionNumber === 3 || this.currentQuestionNumber === 6)) {
          this.showPopupComponent = true;
        } else {
          await this.nextQuestion();
        }
      }, 2000);
    },

    pushUserAnswerArray() {
      const answerObject = {
        msisdn: this.user.msisdn,
        questionCategoryId: this.currentCategoryId || this.userQuestions?.questionCategoryId,
        answer: this.userAnswer,
        questionId: this.userQuestions?.questionId
      };
      this.userAnswerArray.push(answerObject);
    },

    async submitUserAnswer(index) {
      try {
        // game_end CAPTCHA fires only on the final answer of a regular game,
        // mirroring nextQuestion()'s end condition. Never on a bonus round.
        const gameEnd = shouldAttachGameEnd(
            this.userQuestions?.isBonusRound,
            this.currentQuestionNumber,
            this.totalQuestions
        );

        const saveAnswerResult = await TriviaService.submitUserAnswer(
            this.userAnswerArray,
            this.productId,
            this.gameState.GameId,
            this.userQuestions?.isBonusRound,
            this.user.serviceId,
            gameEnd
        );

        const isCorrect = saveAnswerResult?.recordset?.[0]?.Correct;
        if (isCorrect && !this.gameState.IsBonusGame) {
          this.correctAnswersCount++;
          const newTotal = this.regularGameCoins + 10;
          this.setRegularGameCoins(newTotal);
        }
        this.actualCorrectAnswer = saveAnswerResult?.recordset?.[0]?.CorrectAnswer;

        // Find correct answer index
        this.correctAnswerIndex = this.answers.findIndex(
            answer => answer === this.actualCorrectAnswer
        );

        // Update game state
        await this.upsertGameState();

        // Add funds if correct (MTN specific)
        // if (isCorrect && !this.gameState.IsBonusGame) {
        //   await this.addFunds();
        // }
      } catch (error) {
        console.error('Error submitting answer:', error);
      }
    },

    async addFunds(amount) {
      // MTN-specific fund addition
      return await DepService.addFunds(
          this.user.msisdn,
          this.productId,
          7,
          1,
          amount,
          this.gameState.GameId
      );
    },

    async nextQuestion() {
      if (this.currentQuestionNumber >= this.totalQuestions) {
        if (this.gameState.IsRandomize) {
          await this.upsertGameState();
        }
        this.processEndGame();
        return;
      }

      if (this.gameState.GameComplete) {
        this.processEndGame();
        return;
      }

      if (this.gameState.IsBonusGame && this.hasAnswered) {
        this.processEndGame();
        return;
      }

      // Check if current category is complete (skip in random mode - let all categories play through)
      if (!this.gameState.IsRandomize && this.isCategoryComplete(this.currentCategoryId)) {
        this.processEndGame();
        return;
      }

      this.resetQuestionState();
      await this.loadQuestion();

    },

    resetQuestionState() {
      this.hasAnswered = false;
      this.selectedAnswerIndex = null;
      this.correctAnswerIndex = null;
      this.showCorrectDisplay = false;
      this.showIncorrectDisplay = false;
      this.userAnswer = undefined;
      this.userAnswerArray = [];
      this.currentTime = this.maxTime;
      this.startTime = null;
      this.timerExpired = false;
    },

    async loadQuestion(gameStart = false) {
      try {
        this.setIsLoading(true);

        const questionSet = await this.getUserQuestions(gameStart);
        const formattedQuestion = await this.massageQuestionsData(questionSet);

        await this.setUserQuestions(formattedQuestion);

        this.currentQuestion = formattedQuestion.question;
        this.answers = formattedQuestion.answerArray;

        // Open the per-question telemetry window (initial + each next question).
        beginQuestion();

        if (this.gameState.IsBonusGame) {
          this.currentQuestionNumber = 1;
          this.totalQuestions = 1;
        } else {
          this.currentQuestionNumber = formattedQuestion.questionNumber;
          this.totalQuestions = this.getQuestionNumberOfTotal();
        }


        this.currentCategoryId = formattedQuestion.questionCategoryId;
        this.maxTime = formattedQuestion.questionTimer || 20;
        this.currentTime = this.maxTime;

        this.setIsLoading(false);

        if (formattedQuestion.questionCategoryId === 44) {
          this.startTimer();
        } else if (formattedQuestion.questionCategoryId === 46) {
          this.songIsLoading = true;
          await this.playSong();
        }

      } catch (error) {
        console.error('Error loading question:', error);
        this.setIsLoading(false);
      }
    },

    async getUserQuestions(gameStart = false) {
      if (this.gameState.GameComplete) {
        this.setIsLoading(false);
        return;
      }

      const categoryToUse = this.gameState?.SelectedCategory || this.questionCategoryId;
      const genreId = getSeasonalGenreId(categoryToUse);

      const questions = await TriviaService.getTriviaQuestions(
          this.user.msisdn,
          categoryToUse,
          genreId,
          this.productId,
          this.gameState?.IsBonusGame,
          this.user.serviceId,
          gameStart
      );

      await this.upsertGameState();

      if (!questions?.[0]) {
        throw new Error('Invalid question data');
      }

      return questions[0];
    },

    async massageQuestionsData(questionSet) {
      return {
        audioSongLink: questionSet.questionCategoryId === 46 ? questionSet.sourceFile : undefined,
        question: questionSet.question,
        isBonusRound: questionSet.isBonusRound,
        answerArray: questionSet.answers.filter(el => el !== null),
        questionTimer: questionSet.questionTimer || 20,
        gameId: questionSet.gameId,
        questionId: questionSet.questionId,
        questionCategoryId: questionSet.questionCategoryId,
        questionNumber: this.checkUserQuestionNumber(questionSet.questionsAnsweredCount)
      };
    },

    checkUserQuestionNumber(questionsAnsweredCount) {
      return questionsAnsweredCount?.QuestionsAnsweredCount ? questionsAnsweredCount.QuestionsAnsweredCount + 1 : 1;
    },

    // Timer methods
    animate(timestamp) {
      if (!this.startTime) this.startTime = timestamp;

      const elapsed = (timestamp - this.startTime) / 1000;
      this.currentTime = Math.max(0, this.maxTime - elapsed);

      if (this.currentTime > 0 && this.isRunning) {
        this.animationFrame = requestAnimationFrame(this.animate);
      } else if (this.currentTime <= 0) {
        this.currentTime = 0;
        this.handleTimerEnd();
      }
    },

    startTimer() {
      if (!this.isRunning && this.currentTime > 0) {
        this.isRunning = true;
        this.startTime = null;
        this.animationFrame = requestAnimationFrame(this.animate);
      }
    },

    stopTimer() {
      if (this.animationFrame) {
        cancelAnimationFrame(this.animationFrame);
        this.animationFrame = null;
      }
      this.isRunning = false;
    },

    async handleTimerEnd() {
      this.stopTimer();

      // Mark that timer expired
      this.timerExpired = true;

      // Auto-select empty answer if time runs out and user hasn't answered.
      // Backend pr_SaveUserAnswers expects empty string (not null) for a wrong/no answer —
      // null gets dropped and QuestionsAnsweredCount doesn't increment, re-serving the same question.
      if (!this.hasAnswered) {
        // Use -1 to indicate no selection (timeout)
        await this.selectAnswer("", -1);
      }
    },

    // Audio methods for song guessing (category 46)
    async playSong() {
      if (!this.userQuestions?.audioSongLink) {
        console.error('No audio link available');
        this.songIsLoading = false;
        this.startTimer();
        return;
      }

      this.howlerSong = new Howl({
        src: [`${environmentConfig.azureBlob}/${this.userQuestions.audioSongLink}`],
        onplay: () => {
          this.songIsLoading = false;
          this.startTimer();
        },
        onloaderror: () => {
          console.error('Failed to load audio');
          this.songIsLoading = false;
          this.startTimer();
        }
      });

      await this.howlerSong.play();
    },

    stopSong() {
      if (this.howlerSong) {
        Howler.stop();
        Howler.unload();
        this.howlerSong = null;
      }
    },

    // Game state methods
    async upsertGameState() {
      const recordsets = await this.getGameState();
      const firstRecordset = recordsets?.[0] ?? [];
      if (firstRecordset.length > 0) this.setGameState(firstRecordset[0]);
      if (firstRecordset.length < 1) {
        this.setGameState({GameComplete: false});
        this.setCategoryGameState([]);
      }
      if (recordsets?.length > 1) this.setCategoryGameState(recordsets[1]);
    },

    async getGameState() {
      return await TriviaService.getGameState(this.user.msisdn, this.user.serviceId);
    },

    getQuestionNumberOfTotal() {
      const serviceId = mtnServiceIdMap.get(this.user.serviceId);
      if (serviceId === 2) {
        return this.gameState?.IsRandomize ? 14 : 7;
      }
      return 7;
    },

    isCategoryComplete(categoryId) {
      // Bonus rounds are always "complete" after 1 question
      if (this.gameState.IsBonusGame) {
        return true;
      }

      if (this.categoryGameState?.length > 0) {
        const category = this.categoryGameState.find(item =>
            item.QuestionCategoryId === categoryId
        );
        return category?.CategoryComplete === 1;
      }
      return false;
    },

    async processEndGame() {
      // 🟢 PRIORITY 1: Check if we're in a bonus game that just finished
      // This MUST be checked FIRST before anything else!
      if (this.gameState.IsBonusGame && this.hasAnswered) {
        console.log('🎮 Bonus Game Complete!');

        //const statsResponse = await TriviaService.getUserStats(this.user.msisdn, this.productId, this.user.serviceId);
        const baseGameCoins = this.regularGameCoins;
        const answeredCorrectly = this.selectedAnswerIndex === this.correctAnswerIndex;
        let bonusCoins;

        if (answeredCorrectly) {
          // Won bonus round - DOUBLE the coins
          bonusCoins = baseGameCoins * 2;
          console.log('🎮 Bonus Win - Doubled Coins:', bonusCoins);
          this.$router.push({
            name: 'bonus-end-screen',
            query: {
              coins: bonusCoins,
              baseCoins: baseGameCoins
            }
          });
          return; // ← CRITICAL: Stop execution here!
        } else {
          // Lost bonus round - HALVE the coins
          bonusCoins = Math.floor(baseGameCoins / 2);
          console.log('🎮 Bonus Loss - Halved Coins:', bonusCoins);
          this.$router.push({
            name: 'bonus-end-loss',
            query: {
              coins: bonusCoins,
              baseCoins: baseGameCoins
            }
          });
          return; // ← CRITICAL: Stop execution here!
        }
      }

      // 🟢 PRIORITY 2: Check if category is complete
      const currentCategoryComplete = this.isCategoryComplete(this.currentCategoryId);

      // 🟢 PRIORITY 3: Handle regular game (GameComplete = false)
      if (!this.gameState.GameComplete) {
        // Random mode: if all questions answered, route to end screen
        if (this.gameState.IsRandomize && this.currentQuestionNumber >= this.totalQuestions) {
          const totalCoins = this.regularGameCoins || (this.correctAnswersCount * 10);
          const correctCount = Math.floor(totalCoins / 10);
          this.$router.push({
            name: 'end-screen',
            query: {
              correct: correctCount,
              total: this.totalQuestions,
              coins: totalCoins
            }
          });
          return;
        } else {
          if (currentCategoryComplete) {
            const allCategoriesComplete = this.categoryGameState?.every(
                cat => cat.CategoryComplete === 1
            );

            if (allCategoriesComplete) {
              const totalCoins = this.regularGameCoins || (this.correctAnswersCount * 10);
              const correctCount = Math.floor(totalCoins / 10);

              console.log('🎮 Regular Game Complete - Coins:', totalCoins);
              this.$router.push({
                name: 'end-screen',
                query: {
                  correct: correctCount,
                  total: this.totalQuestions,
                  coins: totalCoins
                }
              });
            } else {
              // Not all categories done - return to home
              this.$router.push({name: 'home'});
            }
          }
          return; // Done with regular game logic
        }
      }

      // 🟢 PRIORITY 4: Handle end game (GameComplete = true)
      const serviceId = mtnServiceIdMap.get(this.user.serviceId);

      if (serviceId === 1) {
        // Free users - show end screen
        const totalCoins = this.regularGameCoins || (this.correctAnswersCount * 10);
        const correctCount = Math.floor(totalCoins / 10);
        this.$router.push({
          name: 'end-screen',
          query: {
            correct: correctCount,
            total: this.totalQuestions,
            coins: totalCoins
          }
        });
      } else if (serviceId >= 2) {
        // Paid users - check bonus status
        if (!this.gameState.HasChosenBonusGame) {
          // Haven't started bonus yet - navigate to bonus round selection
          const totalCoins = this.correctAnswersCount * 10;
          this.setRegularGameCoins(totalCoins); // ← STORE THE COINS!
          console.log('🎮 Navigate to Bonus Round - Storing coins:', totalCoins);
          this.$router.push({
            name: 'bonus-round',
            query: {coins: totalCoins}
          });
        } else {
          // Bonus was already completed (coming back from refresh or previous session)
          const statsRecordsets = await TriviaService.getUserStats(this.user.msisdn, this.productId, this.user.serviceId);
          const coins = statsRecordsets?.[0]?.[0]?.Coins;

          if (this.gameState.BonusGameCorrect) {
            this.$router.push({
              name: 'bonus-end-screen',
              query: {coins}
            });
          } else {
            this.$router.push({
              name: 'bonus-end-loss',
              query: {coins}
            });
          }
        }
      }
    },

    async pauseGame() {
      await TriviaService.pauseGame(this.user.msisdn, this.user.serviceId);
    },

    handleExit() {
      this.stopSong();
      this.stopTimer();
      this.pauseGame();
      this.showExitPopup = true;
    },

    handleContinuePlaying() {
      this.showExitPopup = false;
      this.startTimer();
      if (this.userQuestions?.audioSongLink) {
        this.playSong();
      }
    },

    handleExitGame() {
      this.showExitPopup = false;
      this.$router.push({name: 'home'});
    },

    // Event listeners
    setEventListeners() {
      window.addEventListener("beforeunload", this.onRefresh, {once: true});
      window.addEventListener("popstate", this.onBackButton, {once: true});
    },

    onBackButton() {
      this.stopSong();
      this.stopTimer();
      this.pauseGame();
      window.removeEventListener("beforeunload", this.onRefresh);
      window.removeEventListener("popstate", this.onBackButton);
    },

    onRefresh(event) {
      this.stopSong();
      this.stopTimer();
      this.pauseGame();
      const confirmationMessage = 'Are you sure you want to refresh the page?';
      event.returnValue = confirmationMessage;
      return confirmationMessage;
    }
  },

  async beforeMount() {
    try {
      this.setEventListeners();
      this.setIsLoading(true);

      // Initialize game state
      await this.upsertGameState();

      if (this.gameState.IsBonusGame) {
        this.correctAnswersCount = 0;
      }

      // Load first question — mint the game_start CAPTCHA token.
      await this.loadQuestion(true);

    } catch (error) {
      console.error('Failed to initialize game:', error);
      this.setIsLoading(false);
    }
  },
  // TODO:  Add BeforeUnmount clear the (questionCategoryId)
  beforeUnmount() {
    if (this.animationFrame) {
      cancelAnimationFrame(this.animationFrame);
    }
    this.stopSong();
    this.stopTimer();
    this.setQuestionCategoryId(null);
    if (this.questionDelayForAnimation) {
      clearTimeout(this.questionDelayForAnimation);
    }
  }
}
</script>

<style scoped>
.correct-answer {
  border-radius: 10px;
  border: 2px solid #B6E33B;
  background: #8DBE07;
  color: white;
  cursor: not-allowed;
}

.wrong-answer {
  border-radius: 10px;
  border: 2px solid #F00;
  background: #A30808;
  color: white;
  cursor: not-allowed;
}

.unanswered {
  border-radius: 10px;
  border: 2px solid #4A5488;
  background: rgba(255, 255, 255, 0.70);
  color: black;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
}

.unanswered:hover {
  background: rgba(255, 255, 255, 0.85);
  transform: scale(1.02);
}

.unanswered-disabled {
  border-radius: 10px;
  border: 2px solid #4A5488;
  background: rgba(255, 255, 255, 0.40);
  color: #666;
  font-weight: bold;
  cursor: not-allowed;
}

.question-container {
  border-radius: 10px;
  border: 3px solid #4A5488;
  background: rgba(0, 15, 86, 0.40);
  backdrop-filter: blur(13px);
  max-height: 14vh;
  min-height: 14vh;
  margin-top: -1rem;
}

.timer-wrapper {
  position: relative;
  width: 150px;
  height: 150px;
}

.timer-animation {
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.timer-animation::before {
  content: '';
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: conic-gradient(
      var(--timer-color, #C32BE2) var(--timer-progress, 360deg),
      #e0e0e0 var(--timer-progress, 360deg)
  );
  transform: rotate(-90deg);
}

.timer {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  border-radius: 50%;
  width: 87%;
  height: 87%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 48px;
  font-weight: bold;
  color: #333;
  z-index: 1;
}


.incorrect-display-enter {
  animation: vinylScratch 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

@keyframes vinylScratch {
  0% {
    transform: translateX(-100%) rotate(-180deg);
    opacity: 0;
  }
  60% {
    transform: translateX(10%) rotate(10deg);
  }
  100% {
    transform: translateX(0) rotate(0deg);
    opacity: 1;
  }
}

/* Clock image animation - Beat pulse with off-beat shake */
.incorrect-clock-img {
  animation: offBeatPulse 0.8s ease-out;
}

@keyframes offBeatPulse {
  0%, 100% {
    transform: scale(1);
  }
  10% {
    transform: scale(0.8) rotate(-5deg);
  }
  20% {
    transform: scale(1.1) rotate(5deg);
  }
  30% {
    transform: scale(0.95) rotate(-3deg);
  }
  40% {
    transform: scale(1.05) rotate(3deg);
  }
  50% {
    transform: scale(1) rotate(0deg);
  }
}

/* INCORRECT text animation - Broken note/glitch effect */
.incorrect-text {
  animation: brokenNote 0.7s ease-out;
}

@keyframes brokenNote {
  0% {
    transform: translateY(-50px) scale(0);
    opacity: 0;
    filter: blur(10px);
  }
  20% {
    transform: translateY(10px) scale(1.2);
    opacity: 1;
    filter: blur(0px);
  }
  40% {
    transform: translateY(-5px) scale(0.9);
  }
  60% {
    transform: translateY(3px) scale(1.05);
  }
  80% {
    transform: translateY(-2px) scale(0.98);
  }
  100% {
    transform: translateY(0) scale(1);
    opacity: 1;
    filter: blur(0px);
  }
}

/* Coins display animation - Flat/muted note slide */
.coins-display {
  animation: flatNote 0.9s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.2s both;
}

@keyframes flatNote {
  0% {
    transform: translateX(100px) translateY(-20px);
    opacity: 0;
  }
  50% {
    transform: translateX(-10px) translateY(5px);
    opacity: 1;
  }
  100% {
    transform: translateX(0) translateY(0);
    opacity: 1;
  }
}

/* Coin image animation - Wobble like a falling pick */
.coin-img {
  animation: guitarPickWobble 0.8s ease-in-out 0.3s both;
}

@keyframes guitarPickWobble {
  0% {
    transform: rotate(0deg) scale(0);
    opacity: 0;
  }
  30% {
    transform: rotate(180deg) scale(1.2);
    opacity: 1;
  }
  50% {
    transform: rotate(270deg) scale(0.9);
  }
  70% {
    transform: rotate(340deg) scale(1.1);
  }
  100% {
    transform: rotate(360deg) scale(1);
    opacity: 1;
  }
}

/* Alternative: Bass drop shockwave effect for container */
.incorrect-display-bass-drop {
  animation: bassDropShockwave 0.6s ease-out;
}

@keyframes bassDropShockwave {
  0% {
    transform: scale(0.3);
    opacity: 0;
    filter: brightness(2);
  }
  50% {
    transform: scale(1.1);
    opacity: 1;
    filter: brightness(1.2);
  }
  100% {
    transform: scale(1);
    opacity: 1;
    filter: brightness(1);
  }
}

/* Alternative: Record scratch with static effect */
.incorrect-display-scratch {
  animation: recordScratch 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

@keyframes recordScratch {
  0% {
    transform: translateX(-150px) rotate(-90deg) scale(0.5);
    opacity: 0;
    filter: hue-rotate(180deg);
  }
  60% {
    transform: translateX(20px) rotate(10deg) scale(1.1);
    opacity: 1;
  }
  100% {
    transform: translateX(0) rotate(0deg) scale(1);
    opacity: 1;
    filter: hue-rotate(0deg);
  }
}

.correct-display-enter {
  animation: perfectBeatDrop 0.7s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes perfectBeatDrop {
  0% {
    transform: scale(0) rotate(-180deg);
    opacity: 0;
    filter: brightness(3);
  }
  50% {
    transform: scale(1.15) rotate(10deg);
    filter: brightness(1.5);
  }
  100% {
    transform: scale(1) rotate(0deg);
    opacity: 1;
    filter: brightness(1);
  }
}

/* Clock image animation - Hit the note pulse */
.correct-clock-img {
  animation: hitTheNote 0.8s ease-out;
}

@keyframes hitTheNote {
  0% {
    transform: scale(0.5);
    opacity: 0;
  }
  40% {
    transform: scale(1.2);
    opacity: 1;
  }
  60% {
    transform: scale(0.95);
  }
  80% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

/* CORRECT text animation - Rising melody */
.correct-text {
  animation: risingMelody 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes risingMelody {
  0% {
    transform: translateY(50px) scale(0.5);
    opacity: 0;
    filter: blur(5px);
  }
  50% {
    transform: translateY(-10px) scale(1.2);
    opacity: 1;
    filter: blur(0px);
  }
  70% {
    transform: translateY(5px) scale(0.95);
  }
  100% {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
}

/* Coins display animation - Reward fanfare */
.correct-coins-display {
  animation: rewardFanfare 0.9s cubic-bezier(0.175, 0.885, 0.32, 1.275) 0.2s both;
}

@keyframes rewardFanfare {
  0% {
    transform: scale(0) translateY(30px);
    opacity: 0;
  }
  50% {
    transform: scale(1.3) translateY(-5px);
    opacity: 1;
  }
  100% {
    transform: scale(1) translateY(0);
    opacity: 1;
  }
}

/* Coin image animation - Spinning coin collect */
.correct-coin-img {
  animation: coinSpin 1s ease-out 0.3s both;
}

@keyframes coinSpin {
  0% {
    transform: rotateY(0deg) scale(0);
    opacity: 0;
  }
  30% {
    transform: rotateY(360deg) scale(1.3);
    opacity: 1;
  }
  50% {
    transform: rotateY(540deg) scale(1.1);
  }
  70% {
    transform: rotateY(660deg) scale(1.15);
  }
  100% {
    transform: rotateY(720deg) scale(1);
    opacity: 1;
  }
}

/* Alternative: Concert lights flash effect */
.correct-display-lights {
  animation: concertLights 0.8s ease-out;
}

@keyframes concertLights {
  0% {
    transform: scale(0.8);
    opacity: 0;
    filter: brightness(0.5) saturate(0);
  }
  20% {
    filter: brightness(2) saturate(2);
  }
  40% {
    transform: scale(1.1);
    filter: brightness(1.5) saturate(1.5);
  }
  60% {
    filter: brightness(1.2) saturate(1.2);
  }
  100% {
    transform: scale(1);
    opacity: 1;
    filter: brightness(1) saturate(1);
  }
}

/* Alternative: Champion note sustain */
.correct-display-sustain {
  animation: noteSustain 0.9s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

@keyframes noteSustain {
  0% {
    transform: scale(0.3) translateY(-100px);
    opacity: 0;
    filter: blur(10px) brightness(2);
  }
  40% {
    transform: scale(1.1) translateY(10px);
    opacity: 1;
    filter: blur(0px) brightness(1.3);
  }
  70% {
    transform: scale(0.95) translateY(-5px);
  }
  100% {
    transform: scale(1) translateY(0);
    opacity: 1;
    filter: blur(0px) brightness(1);
  }
}

/* Shimmer effect overlay (optional - add as pseudo element) */
@keyframes shimmerEffect {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

/* Add this class for extra sparkle on the container */
.correct-shimmer::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.3) 50%,
      transparent 100%
  );
  background-size: 200% 100%;
  animation: shimmerEffect 1s ease-out 0.2s;
  pointer-events: none;
}

/* Hide footer when screen height is too small */
@media (max-height: 700px) {
  .game-footer {
    display: none !important;
  }
}

/* Optional: Adjust other elements when footer is hidden */
@media (max-height: 700px) {
  .game-footer {
    display: none !important;
  }

  /* Remove bottom padding from main container since footer is gone */
  .d-flex.flex-column.justify-content-start.align-items-center.col-12.position-relative {
    padding-bottom: 20px;
  }
}
</style>